<template>
  <BaseIcon
    :showIcon="showIcon"
    :icon="batteryIcon"
    :iconColor="color"
    imgAlt="Battery Icon"
    :iconText="iconText"
    tooltipContentClass="battery-status-tooltip"
    dataCy="batteryStatus"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import BaseIcon from './BaseIcon.vue'

export default defineComponent({
  name: 'BatteryStatus',
  components: {
    BaseIcon
  },
  props: {
    deviceId: { type: String, default: '' }
  },
  computed: {
    smartBridgeDeviceId(): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_ID_BY_DEVICE_ID}`
      ](this.deviceId)
    },
    batteryChargeRounded(): number {
      if (typeof this.batteryRemaining !== 'number') {
        return 0
      }
      const roundedResult = Math.round(this.batteryRemaining / 10) * 10
      if (roundedResult >= 100) {
        return 100
      }
      if (roundedResult <= 0) {
        return 0
      }
      return roundedResult
    },
    isDeviceConnectedToSmartBridge(): boolean {
      return !!this.smartBridgeDeviceId
    },
    isThereABatteryState(): boolean {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_THERE_A_VALUE}`](
        this.deviceId,
        'batteryInstalled'
      )
    },
    isDeviceConnected(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
      ](this.deviceId)
    },
    isSmartBridgeConnected(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
      ](this.smartBridgeDeviceId)
    },
    batteryRemaining(): number {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_BATTERY_REMAINING_BY_DEVICE_ID}`
      ](this.deviceId)
    },
    isBatteryCharging(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_BATTERY_CHARGING_BY_DEVICE_ID}`
      ](this.deviceId)
    },
    isBatteryInstalled(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_BATTERY_INSTALLED_BY_DEVICE_ID}`
      ](this.deviceId)
    },
    iconText(): string {
      if (!this.isThereABatteryState) {
        return ''
      }
      if (!this.isBatteryInstalled) {
        return this.$t('device.icons.batteryStatus.noBatteryInstalled')
      }
      return `${this.batteryRemaining}%`
    },
    batteryIcon(): string {
      if (!this.isBatteryInstalled) {
        return 'mdi-battery-off-outline'
      }
      if (this.isBatteryCharging) {
        return 'mdi-battery-charging'
      }
      return this.batteryIconFromChargeRounded
    },
    batteryIconFromChargeRounded(): string {
      let icon = ''
      const chargeRounded = this.batteryChargeRounded
      switch (chargeRounded) {
        case 0: {
          icon = 'mdi-battery-outline'
          break
        }
        case 100: {
          icon = 'mdi-battery'
          break
        }
        default: {
          icon = `mdi-battery-${chargeRounded}`
        }
      }
      return icon
    },
    color(): string {
      return this.batteryRemaining <= 20 && !this.isBatteryCharging && this.isBatteryInstalled
        ? 'var(--tsi-color--accent-error)'
        : 'grey'
    },

    showIcon(): boolean {
      return (
        this.isDeviceConnectedToSmartBridge &&
        this.isThereABatteryState &&
        this.isSmartBridgeConnected &&
        this.isDeviceConnected
      )
    }
  }
})
</script>
